import * as Color from "color"

export const HSL_HUE_MIN = 0
export const HSL_HUE_MAX = 360

/**
 * Provides methods to generate colors.
 */
export class ColorsHelper {
	/**
	 * Gets the pastel color from the given hue.
	 * @param {Number} [hue]     The hue. An integer value between 0 and 255.
	 * If it is not specified, a random value is set.
	 * If the hue is less than 0 or greater than 255, it is computed to be the difference with 255.
	 * Examples:
	 * - If the hue is 265, it is computed to be 10 (265 - 360)
	 * - If the hue is -10, it is computed to be 350 (360 -10)
	 * @param {Number} [opacity] The opacity. An floating value between 0 and 1.
	 * @returns A HSL syntaxed color.
	 */
	static getPastelColor(hue, opacity = 1) {
		hue = hue || Math.round(Math.random() * 256)
		while (hue < HSL_HUE_MIN) hue += HSL_HUE_MAX
		while (hue > HSL_HUE_MAX) hue -= HSL_HUE_MAX
		return opacity < 0 || opacity >= 1
			? `hsl(${hue}, 100%, 75%)`
			: `hsla(${hue}, 100%, 75%, ${opacity})`
	}

	/**
	 * Gets an array of pastel colors.
	 * @param {Number} length The number of colors to produce.
	 * @param {Number} startHue The hue. An integer value between 0 and 255.
	 * @returns {String[]} An array of HSL syntaxed colors.
	 */
	static getEquidistantPastelColors(length, startHue) {
		startHue = startHue || 0

		const colors = []
		const offset = Math.round(HSL_HUE_MAX / length)

		for (let i = 0; i < length; i++) {
			const hue   = startHue + i * offset
			colors.push(ColorsHelper.getPastelColor(hue))
		}

		return colors
	}

	/**
	 * Obtient un tuple de couleurs d'harmonie analogue dont la première est la
	 * couleur clé `keyColor`.
	 * @param keyColor
	 * @param harmony
	 * @param length
	 */
	static getHarmonicColors(
		keyColor: Color,
		harmony: "analogous",
		length: 2
	): [Color, Color]
	/**
	 * Obtient un triplet de couleurs d'harmonie analogue dont la première est la
	 * couleur clé `keyColor`.
	 * @param keyColor
	 * @param harmony
	 * @param length
	 */
	static getHarmonicColors(
		keyColor: Color,
		harmony: "analogous",
		length: 3
	): [Color, Color, Color]
	/**
	 * Obtient un tuple de couleurs d'harmonie complémentaire dont la première est
	 * la couleur clé `keyColor`.
	 * @param keyColor
	 * @param harmony
	 */
	static getHarmonicColors(
		keyColor: Color,
		harmony: "complementary"
	): [Color, Color]
	/**
	 * Obtient un triplet de couleurs d'harmonie scission de complémentaire dont
	 * la première est la couleur clé `keyColor`.
	 * @param keyColor
	 * @param harmony
	 */
	static getHarmonicColors(
		keyColor: Color,
		harmony: "split-complementary"
	): [Color, Color, Color]
	/**
	 * Obtient un triplet de couleurs d'harmonie triadique dont la première est la
	 * couleur clé `keyColor`.
	 * @param keyColor
	 * @param harmony
	 */
	static getHarmonicColors(
		keyColor: Color,
		harmony: "triad"
	): [Color, Color, Color]
	/**
	 * Obtient un quadruplet de couleurs d'harmonie tetrahédrique dont la première
	 * est la couleur clé `keyColor`.
	 * @param keyColor
	 * @param harmony
	 */
	static getHarmonicColors(
		keyColor: Color,
		harmony: "tetrahedral"
	): [Color, Color, Color, Color]
	/**
	 * Obtient une liste de `length` couleurs réparties équitablement sur le
	 * cercle chromatique et dont la première est la couleur clé `keyColor`.
	 * @param keyColor
	 * @param length
	 */
	static getHarmonicColors(
		keyColor: Color,
		length: number
	): Color[]
	static getHarmonicColors(
		keyColor: Color,
		harmonyOrLength?: Harmony | number,
		length?: number
	): Color[] {
		const siblingAngle = 360 / 12

		if (typeof harmonyOrLength === "number") {
			length = harmonyOrLength
			const colors: Color[] = [keyColor]
			if (!length || 1 === length) return colors

			const angle = 360 / length

			for (let i = 1; i < length; i++)
				colors.push(new Color(keyColor).rotate(i * angle))

			return colors
		}

		switch (harmonyOrLength) {
			case "analogous":
				return 2 === length
				? [keyColor, new Color(keyColor).rotate(siblingAngle)]
				: [
					keyColor,
					new Color(keyColor).rotate(-siblingAngle),
					new Color(keyColor).rotate(siblingAngle)
				]
			case "complementary":
				return [keyColor, new Color(keyColor).rotate(180)]
			case "split-complementary":
				return [
					keyColor,
					new Color(keyColor).rotate(180 - siblingAngle),
					new Color(keyColor).rotate(180 + siblingAngle)
				]
			case "tetrahedral":
				return [
					keyColor,
					new Color(keyColor).rotate(90),
					new Color(keyColor).rotate(180),
					new Color(keyColor).rotate(270)
				]
			case "triad":
				return [
					keyColor,
					new Color(keyColor).rotate(120),
					new Color(keyColor).rotate(-120)
				]
		}
	}
}

export type Harmony = "analogous"
| "complementary"
| "split-complementary"
| "tetrahedral"
| "triad"



// WEBPACK FOOTER //
// ./src/util/colors.ts